import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from '../api/axios';
import Toast from '../components/Toast';
import '../pages/PublisherEditInline.css';

export default function PublisherEditInline({ applicationId, onClose }) {
  const locale = localStorage.getItem('locale') || 'kz';
  const preferKz = ['kk', 'kz'].includes(locale);

  const [form, setForm] = useState(null);
  const [refs, setRefs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [blocked, setBlocked] = useState(false);

  const [busy, setBusy] = useState({});
  const isBusy = (k) => !!busy[k];
  const setBusyKey = (k, v) => setBusy((p) => ({ ...p, [k]: v }));
  const withLoader = async (key, fn) => {
    setBusyKey(key, true);
    try {
      await fn();
    } finally {
      setBusyKey(key, false);
    }
  };
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));

  const L = preferKz
    ? { loading: 'Жүктелуде…', saving: 'Сақталуда…', deleting: 'Өшірілуде…', adding: 'Қосылуда…' }
    : { loading: 'Загрузка…', saving: 'Сохранение…', deleting: 'Удаление…', adding: 'Добавление…' };

  const BusyButton = ({
    busyKey,
    onClick,
    className = '',
    busyLabel,
    children,
    disabled,
    ...rest
  }) => (
    <button
      type="button"
      className={`${className} ${isBusy(busyKey) ? 'is-loading' : ''}`}
      disabled={!!disabled || isBusy(busyKey)}
      onClick={() => withLoader(busyKey, onClick)}
      {...rest}
    >
      {isBusy(busyKey) ? (
        <>
          <span className="btn-spinner" /> {busyLabel || L.loading}
        </>
      ) : (
        children
      )}
    </button>
  );

  // files state
  const [existingRows, setExistingRows] = useState([]);
  const [initialExistingRows, setInitialExistingRows] = useState([]); // snapshot from server
  const [replaceBySection, setReplaceBySection] = useState({}); // { [section]: File }
  const [existingArchives, setExistingArchives] = useState({}); // { [section]: File }
const existingArchivePickers = useRef({});
  const [deleteFlags, setDeleteFlags] = useState({}); // { [section]: boolean }
  const [newRows, setNewRows] = useState([]); // append candidates or replace payload additions

  // archive
  const [pendingArchive, setPendingArchive] = useState(null);
  const archivePickerRef = useRef(null);

  // file inputs refs
  const replacePickers = useRef({});
  const newPickers = useRef({});

  const endpoints = useMemo(() => ['criteria-forms'], []);

  const ALLOWED_FIELDS = [
    'authors',
    'message',
    'criteria_form_id',
    'section_count',
    'page_count',
    'sheet_count',
    'duration_minutes',
  ];

  const t = useMemo(
    () =>
      preferKz
        ? {
            loading: 'Жүктелуде...',
            notFound: 'Дерек табылмады',
            noAccess: 'Бұл өтінімді өңдеуге рұқсат жоқ.',
            close: 'Жабу',
            save: 'Сақтау',

            sectionMain: 'Негізгі ақпарат',
            sectionParams: 'Қосымша параметрлер',
            sectionArchive: 'Архив',
            sectionFiles: 'Жүктелген файлдар',
            sectionNewFiles: 'Жаңа файлдар',
            sectionMessage: 'Хабарлама',

            authors: 'Авторлар',
            criteriaForm: 'Форма әзірлеу',
            choose: 'Таңдаңыз',

            sectionCount: 'Бөлім саны',
            sectionCountHint: 'Автоматты түрде есептеледі',
            pageCount: 'Бет саны',
            sheetCount: 'Парақ саны',
            durationMinutes: 'Ұзақтығы (мин)',

            archiveMissing: 'Архив жоқ',
            archiveDownload: 'Жүктеу',
            archiveReplace: 'Ауыстыру',
            archiveAdd: 'Қосу',
            archiveClear: 'Тазалау',
            archiveUnsaved: 'Сақталмаған жаңа архив',

            filesLoaded: 'Жүктелген файлдар:',
            fileName: 'Файл атауы',
            fileLink: 'Сілтеме (электронды нұсқа)',
            filePick: 'Файл таңдау',
            fileDownload: 'Жүктеу',
            fileDelete: 'Жою',
            fileUndoDelete: 'Қалпына келтіру',

            addFile: 'Файл қосу',
            fileOrLinkName: 'Файл немесе сілтеме атауы',
            noFileChosen: 'Файл таңдалмады',
            fileLinkForElectronic: 'Сілтеме (электронды нұсқа үшін)',
            fileSection: 'Бөлім',

            messagePlaceholder: 'Хабарлама',

            toastLoadError: 'Жүктеу кезінде қате',
            toastDownloadFileError: 'Файлды жүктеу мүмкін емес',
            toastDownloadArchiveError: 'Архивті жүктеу мүмкін емес',
            toastSaveSuccess: 'Сәтті сақталды!',
            toastSaveError: 'Қате орын алды!',
            toastFixValidation: 'Файл/сілтеме деректерін тексеріңіз',
            toastDuplicateSection: 'Бірдей бөлім нөмірі қайталануда',
            toastAppendConflict: 'Жаңа файл бөлімі бұрыннан бар (append режимі)',
            toastInvalidSection: 'Бөлім нөмірі қате',

            confirmDeleteSection: 'Секциялық өшіру?',
          }
        : {
            loading: 'Загрузка...',
            notFound: 'Данные не найдены',
            noAccess: 'Редактирование этой заявки недоступно.',
            close: 'Закрыть',
            save: 'Сохранить',

            sectionMain: 'Основная информация',
            sectionParams: 'Дополнительные параметры',
            sectionArchive: 'Архив',
            sectionFiles: 'Загруженные файлы',
            sectionNewFiles: 'Новые файлы',
            sectionMessage: 'Сообщение',

            authors: 'Авторы',
            criteriaForm: 'Форма разработки',
            choose: 'Выберите',

            sectionCount: 'Количество разделов',
            sectionCountHint: 'Рассчитывается автоматически',
            pageCount: 'Количество страниц',
            sheetCount: 'Количество листов',
            durationMinutes: 'Длительность (мин)',

            archiveMissing: 'Архив отсутствует',
            archiveDownload: 'Скачать',
            archiveReplace: 'Заменить',
            archiveAdd: 'Добавить',
            archiveClear: 'Очистить',
            archiveUnsaved: 'Новый архив (не сохранён)',

            filesLoaded: 'Загруженные файлы:',
            fileName: 'Название файла',
            fileLink: 'Ссылка (для эл. версии)',
            filePick: 'Выбрать файл',
            fileDownload: 'Скачать',
            fileDelete: 'Удалить',
            fileUndoDelete: 'Отменить удаление',

            addFile: 'Добавить файл',
            fileOrLinkName: 'Название файла или ссылки',
            noFileChosen: 'Файл не выбран',
            fileLinkForElectronic: 'Ссылка (для электронной версии)',
            fileSection: 'Раздел',

            messagePlaceholder: 'Сообщение',

            toastLoadError: 'Ошибка при загрузке',
            toastDownloadFileError: 'Не удалось скачать файл',
            toastDownloadArchiveError: 'Не удалось скачать архив',
            toastSaveSuccess: 'Сохранено успешно',
            toastSaveError: 'Произошла ошибка',
            toastFixValidation: 'Проверьте данные файла/ссылки',
            toastDuplicateSection: 'Повторяется номер раздела',
            toastAppendConflict: 'Раздел нового файла уже существует (append режим)',
            toastInvalidSection: 'Некорректный номер раздела',

            confirmDeleteSection: 'Удалить секцию?',
          },
    [preferKz]
  );

  const textFrom = (item) => {
    if (!item) return '';
    if (preferKz) {
      return item.name_kz ?? item.name_kk ?? item.kz ?? item.kk ?? item.name ?? item.title ?? '';
    }
    return item.name_ru ?? item.ru ?? item.name ?? item.title ?? item.name_kz ?? item.name_kk ?? '';
  };

  const equalsLoose = (a, b) =>
    String(a ?? '').trim().toLowerCase() === String(b ?? '').trim().toLowerCase();

  const isNumLike = (v) => typeof v === 'number' || /^\d+$/.test(String(v ?? ''));

  const findIdByValue = (list = [], value) => {
    if (value === 0 || value) {
      if (typeof value === 'object' && value?.id != null) return String(value.id);
      if (isNumLike(value)) return String(value);

      for (const it of list) {
        const kzLike = it?.kk ?? it?.kz ?? it?.name_kz ?? it?.name_kk ?? it?.title_kz ?? it?.title_kk;
        const ruLike = it?.ru ?? it?.name_ru ?? it?.title_ru;
        const primary = preferKz ? kzLike ?? ruLike : ruLike ?? kzLike;
        const fallback = it?.name ?? it?.title;

        if (equalsLoose(primary, value) || equalsLoose(fallback, value)) return String(it.id);
      }
    }
    return '';
  };

  const norm = (v) => (v === undefined || v === null ? '' : v);

  const downloadBlob = (blob, filename = 'download') => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.setAttribute('download', filename);
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const normalizeToForm = (app, dicts) => {
    const f = { ...app };

    f.authors = app?.authors ?? '';
    f.message = app?.message === 'null' ? '' : app?.message ?? '';

    f.criteria_form_id = findIdByValue(
      dicts['criteria-forms'],
      app?.criteria_form_id ?? app?.criteria_form ?? ''
    );

    f.section_count = app?.section_count ?? '';
    f.page_count = app?.page_count ?? '';
    f.sheet_count = app?.sheet_count ?? '';
    f.duration_minutes = app?.duration_minutes ?? '';
    f.archive = app?.archive || null;

    return f;
  };

  const normalizeExistingFiles = (files = []) =>
    (files || [])
      .map((f) => {
        const section = Number(f.section ?? f.section_number ?? 0);
        const isLink = Boolean(f.is_link) || !!(f.link_url ?? f.file_link);
        const url = f.url ?? f.download_url ?? f.file_url ?? null;
        const name = f.name ?? f.application_file_name ?? f.original_name ?? '';
        const linkUrl = (f.link_url ?? f.file_link ?? '') || '';

         return {
          id: Number(f.id),
          section,
          is_link: isLink,
          type: isLink ? 'link' : 'upload',
          url,
          name,
          link_url: linkUrl,
          file_path: f.file_path ?? null,
          section_archive_name: f.section_archive_original_name || f.section_archive_name || null,
        };
      })
      .filter((r) => Number.isFinite(r.id) && Number.isFinite(r.section) && r.section > 0)
      .sort((a, b) => a.section - b.section);

  const existingFingerprintMap = (rows = []) => {
    const map = new Map();
    for (const r of rows) {
      map.set(Number(r.id), {
        id: Number(r.id),
        section: Number(r.section),
        name: String(r.name || ''),
        link_url: String(r.link_url || '').trim(),
      });
    }
    return map;
  };

  const getActiveSectionCount = (existingRowsArg, newRowsArg, deleteFlagsArg) => {
    const used = new Set();

    for (const r of existingRowsArg) {
      const s = Number(r.section);
      if (!s) continue;
      if (!deleteFlagsArg[s]) used.add(s);
    }

    for (const r of newRowsArg) {
      const s = Number(r.section);
      if (!s) continue;
      if (r.file || (r.link_url && r.link_url.trim())) used.add(s);
    }

    return used.size;
  };

  useEffect(() => {
    let alive = true;

    (async () => {
      setLoading(true);
      setBlocked(false);

      try {
        const [appRes, ...refsRes] = await Promise.all([
          axios.get(`/publisher/applications/${applicationId}`),
          ...endpoints.map((ep) => axios.get(`/publisher/ref/${ep}`)),
        ]);

        if (!alive) return;

        const refsMap = {};
        endpoints.forEach((ep, i) => {
          refsMap[ep] = Array.isArray(refsRes[i]?.data) ? refsRes[i].data : refsRes[i]?.data?.data || [];
        });
        setRefs(refsMap);

        const app = appRes?.data?.application || appRes?.data || {};
        const normalizedExisting = normalizeExistingFiles(app?.files || []);

        setForm(normalizeToForm(app, refsMap));
        setExistingRows(normalizedExisting);
        setInitialExistingRows(normalizedExisting);
        setReplaceBySection({});
        setDeleteFlags({});
        setNewRows([]);
        setPendingArchive(null);
      } catch (e) {
        console.error('Load error:', e);
        if (!alive) return;

        if (e?.response?.status === 403) {
          setBlocked(true);
          setForm(null);
        } else {
          setToast({ message: t.toastLoadError, type: 'error' });
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [applicationId, endpoints, t.toastLoadError]);

  useEffect(() => {
    const count = getActiveSectionCount(existingRows, newRows, deleteFlags);
    setForm((prev) => {
      if (!prev) return prev;
      if (Number(prev.section_count ?? 0) === count) return prev;
      return { ...prev, section_count: count };
    });
  }, [existingRows, newRows, deleteFlags]);

  const onChange = (e) => {
    const { name, value, type } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? '' : Number(value)) : value,
    }));
  };

  const setExisting = (fileId, patch) =>
    setExistingRows((prev) => prev.map((r) => (r.id === fileId ? { ...r, ...patch } : r)));

  const setDeleteFlag = (section, val) =>
    setDeleteFlags((prev) => ({ ...prev, [section]: !!val }));

  const onReplacePicked = (section, e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;

    setReplaceBySection((prev) => ({ ...prev, [section]: f }));

    setExistingRows((prev) =>
      prev.map((r) =>
        r.section === section
          ? {
              ...r,
              name: r.name || f.name,
              link_url: '',
              is_link: false,
              type: 'upload',
            }
          : r
      )
    );

    setDeleteFlag(section, false);
  };

  const nextSection = () => {
    const used = new Set([
      ...existingRows.filter((r) => !deleteFlags[r.section]).map((r) => Number(r.section)).filter(Boolean),
      ...newRows.map((r) => Number(r.section)).filter(Boolean),
    ]);
    let n = 1;
    while (used.has(n)) n++;
    return n;
  };

  const addNewRow = () =>
    setNewRows((prev) => [
      ...prev,
      { uid: `${Date.now()}_${prev.length}`, section: nextSection(), name: '', link_url: '', file: null, section_archive: null }
    ]);

  const removeNewRow = (uid) => setNewRows((prev) => prev.filter((r) => r.uid !== uid));
  const setNewRow = (uid, patch) =>
    setNewRows((prev) => prev.map((r) => (r.uid === uid ? { ...r, ...patch } : r)));

  const handleDownloadFile = async ({ id, link_url, name, url }) => {
    try {
      if (link_url) {
        window.open(link_url, '_blank', 'noopener,noreferrer');
        return;
      }
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
        return;
      }
      if (!id) throw new Error('missing id');

      const res = await axios.get(`/publisher/files/${id}`, { responseType: 'blob' });
      const disp = res.headers['content-disposition'];

      let suggested = name || `file-${id}`;
      const m = disp ? /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(disp) : null;
      if (m?.[1]) suggested = decodeURIComponent(m[1]);

      downloadBlob(res.data, suggested);
    } catch (err) {
      console.error('download error', err);
      setToast({ message: t.toastDownloadFileError, type: 'error' });
    }
  };

  const handleDownloadArchive = async () => {
    try {
      const res = await axios.get(`/publisher/applications/${applicationId}/download-archive`, {
        responseType: 'blob',
      });

      const disp = res.headers['content-disposition'];
      let filename =
        form?.archive?.original_name ||
        (disp && /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(disp)?.[1]) ||
        `application-${applicationId}.zip`;

      filename = decodeURIComponent(filename);
      downloadBlob(res.data, filename);
    } catch (e) {
      const webUrl = form?.archive?.download_url;
      if (webUrl) {
        const a = document.createElement('a');
        a.href = webUrl;
        a.target = '_blank';
        a.rel = 'noopener,noreferrer';
        document.body.appendChild(a);
        a.click();
        a.remove();
        return;
      }

      console.error('archive download error', e);
      setToast({ message: t.toastDownloadArchiveError, type: 'error' });
    }
  };

  const onArchivePicked = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setPendingArchive(file);
    setForm((prev) => ({
      ...prev,
      archive: {
        ...(prev?.archive || {}),
        original_name: file.name,
        size: file.size,
      },
    }));
  };

  const clearPendingArchive = () => {
    setPendingArchive(null);
    setForm((prev) => ({
      ...prev,
      archive: prev?.archive?.path ? prev.archive : null,
    }));
  };

  const deleteSectionNow = async (section) => {
    if (!window.confirm(t.confirmDeleteSection)) return;

    setDeleteFlags((prev) => ({ ...prev, [section]: !prev[section] }));

    // если удаляем секцию — снятие replace-файла для неё (чтобы не держать лишний file)
    setReplaceBySection((prev) => {
      const copy = { ...prev };
      delete copy[section];
      return copy;
    });
  };

  // ===== file mode detection =====
  const hasDuplicateSectionsInNewRows = () => {
    const seen = new Set();
    for (const r of newRows) {
      const s = Number(r.section);
      if (!s) continue;
      if (seen.has(s)) return true;
      seen.add(s);
    }
    return false;
  };

  const hasNewRowsPayload = () =>
    newRows.some((r) => Number(r.section) > 0 && (!!r.file || !!String(r.link_url || '').trim()));

  const hasExistingDeletes = () => Object.values(deleteFlags).some(Boolean);

  const hasExistingReplaces = () => Object.keys(replaceBySection).some((k) => !!replaceBySection[k]);

  const hasExistingMetaEdits = () => {
    const initialMap = existingFingerprintMap(initialExistingRows);

    for (const row of existingRows) {
      const base = initialMap.get(Number(row.id));
      if (!base) return true;

      const curSection = Number(row.section);
      const curName = String(row.name || '');
      const curLink = String(row.link_url || '').trim();

      if (curSection !== Number(base.section)) return true;
      if (curName !== String(base.name || '')) return true;
      if (curLink !== String(base.link_url || '')) return true;
    }

    return false;
  };

  const hasExistingArchiveChanges = () =>
  Object.values(existingArchives).some(Boolean);

  const getFileChangesMode = () => {
    const needReplace = hasExistingDeletes() || hasExistingReplaces() || hasExistingMetaEdits() || hasExistingArchiveChanges();

    if (needReplace) return 'replace';
    if (hasNewRowsPayload()) return 'append';
    return 'none';
  };

  // ===== builders =====
  const buildAppendRows = () => {
    return newRows
      .map((r) => {
        const section = Number(r.section);
        const file = r.file || null;
        const link = String(r.link_url || '').trim();
        const hasPayload = !!file || !!link;
        if (!section || !hasPayload) return null;

        return {
  section_number: section,
  name: (r.name || (file ? file.name : '')).trim(),
  file_link: file ? '' : link,
  file,
  section_archive: r.section_archive || null,
};
      })
      .filter(Boolean)
      .sort((a, b) => a.section_number - b.section_number);
  };

  const buildReplaceRows = () => {
    const bySection = new Map();

    // 1) existing rows (keep all except deleted)
    for (const row of existingRows) {
      const section = Number(row.section);
      if (!section || deleteFlags[section]) continue;

      bySection.set(section, {
        id: row.id,
        section_number: section,
        name: String(row.name || '').trim(),
        file_link: String(row.link_url || '').trim(),
        file: null,
        section_archive: existingArchives[section] || null,
        existing: true,
      });
    }

    // 2) file replacements for existing section
    for (const [sectionKey, file] of Object.entries(replaceBySection || {})) {
      const section = Number(sectionKey);
      if (!section || !file || deleteFlags[section]) continue;

      const prev = bySection.get(section);

      bySection.set(section, {
        id: prev?.id || null,
        section_number: section,
        name: String((prev?.name || file.name) ?? '').trim(),
        file_link: '',
        file,
        section_archive: existingArchives[section] || null,
        existing: !!prev?.id,
      });
    }

    // 3) include new rows too (mixed ops in replace mode)
    for (const r of newRows) {
      const section = Number(r.section);
      const file = r.file || null;
      const link = String(r.link_url || '').trim();
      const hasPayload = !!file || !!link;
      if (!section || !hasPayload) continue;
      if (deleteFlags[section]) continue;

      const prev = bySection.get(section);

       bySection.set(section, {
        id: prev?.id || null,
        section_number: section,
        name: String((r.name || (file ? file.name : prev?.name || '')) ?? '').trim(),
        file_link: file ? '' : link,
        file,
        section_archive: r.section_archive || null,
        existing: !!prev?.id,
      });
    }

    return [...bySection.values()].sort((a, b) => a.section_number - b.section_number);
  };

  // ===== validators =====
  const validateAppendRows = (rows) => {
    if (hasDuplicateSectionsInNewRows()) {
      setToast({ message: t.toastDuplicateSection, type: 'error' });
      return false;
    }

    const existingSections = new Set(
      existingRows
        .filter((r) => !deleteFlags[r.section]) // deleteFlags usually false in append mode, but safe
        .map((r) => Number(r.section))
        .filter(Boolean)
    );

    for (const r of rows) {
      const hasFile = !!r.file;
      const hasLink = !!String(r.file_link || '').trim();

      if (!r.section_number || r.section_number < 1) {
        setToast({ message: t.toastInvalidSection, type: 'error' });
        return false;
      }

      if (!hasFile && !hasLink) {
        setToast({ message: t.toastFixValidation, type: 'error' });
        return false;
      }

      // append mode should not target existing section
      if (existingSections.has(Number(r.section_number))) {
        setToast({ message: t.toastAppendConflict, type: 'error' });
        return false;
      }
    }

    return true;
  };

  const validateReplaceRows = (rows) => {
    const seenSections = new Set();

    for (const r of rows) {
      const hasFile = !!r.file;
      const hasLink = !!String(r.file_link || '').trim();
      const hasId = !!r.id;

      if (!r.section_number || r.section_number < 1) {
        setToast({ message: t.toastInvalidSection, type: 'error' });
        return false;
      }

      if (seenSections.has(Number(r.section_number))) {
        setToast({ message: t.toastDuplicateSection, type: 'error' });
        return false;
      }
      seenSections.add(Number(r.section_number));

      // existing row may be sent with id only (keep old file/link)
      if (!hasId && !hasFile && !hasLink) {
        setToast({ message: t.toastFixValidation, type: 'error' });
        return false;
      }
    }

    return true;
  };

  const appendFilesToFormData = (fd, rows, { replaceFiles }) => {
    fd.append('replace_files', replaceFiles ? '1' : '0');

    rows.forEach((r, i) => {
      if (r.id) fd.append(`files[${i}][id]`, String(r.id));
      fd.append(`files[${i}][section_number]`, String(r.section_number));

      if (r.name) fd.append(`files[${i}][name]`, r.name);

      if (String(r.file_link || '').trim()) {
        fd.append(`files[${i}][file_link]`, String(r.file_link).trim());
      }

      if (r.file) {
        fd.append(`files[${i}][file]`, r.file);
      }
      if (r.section_archive) {
        fd.append(`files[${i}][section_archive]`, r.section_archive);
      }
    });
  };

  const debugLogFormData = (fd) => {
    // Можно оставить включенным на время отладки
    // eslint-disable-next-line no-console
    console.group('PublisherEditInline FormData');
    for (const [k, v] of fd.entries()) {
      // eslint-disable-next-line no-console
      console.log(k, v instanceof File ? `[File:${v.name}; ${v.size}]` : v);
    }
    // eslint-disable-next-line no-console
    console.groupEnd();
  };

  const handleSubmit = async () => {
    setSaving(true);

    try {
      const fd = new FormData();

      ALLOWED_FIELDS.forEach((k) => {
        if (k in (form || {})) fd.append(k, norm(form[k]));
      });

      if (pendingArchive) {
        fd.append('archive', pendingArchive);
      }

      const mode = getFileChangesMode(); // 'none' | 'append' | 'replace'

      if (mode === 'append') {
        const rows = buildAppendRows();
        if (!validateAppendRows(rows)) {
          setSaving(false);
          return;
        }
        if (rows.length > 0) {
          appendFilesToFormData(fd, rows, { replaceFiles: false });
        }
      } else if (mode === 'replace') {
        const rows = buildReplaceRows();
        if (!validateReplaceRows(rows)) {
          setSaving(false);
          
          return;
        }
        appendFilesToFormData(fd, rows, { replaceFiles: true });
      }

      // debug
      // debugLogFormData(fd);

      await axios.post(`/publisher/applications/${applicationId}/update`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setToast({ message: t.toastSaveSuccess, type: 'success' });
      onClose?.();
    } catch (e) {
      console.error('Update error:', e?.response?.data || e);

      const apiErrors = e?.response?.data?.errors;
      const firstErr = apiErrors ? Object.values(apiErrors)?.[0]?.[0] : null;

      setToast({
        message: firstErr || t.toastSaveError,
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const Select = ({ name, label, source }) => {
    const options = refs[source] || [];
    const value = form?.[name] != null ? String(form[name]) : '';

    return (
      <div className="form-group">
        <label>{label}</label>
        <select name={name} value={value} onChange={onChange}>
          <option value="">{t.choose}</option>
          {options.map((o) => (
            <option key={o.id} value={String(o.id)}>
              {textFrom(o)}
            </option>
          ))}
        </select>
      </div>
    );
  };

  if (blocked) {
    return (
      <div style={{ padding: '1rem', color: 'darkred' }}>
        {t.noAccess}
        <br />
        <button type="button" onClick={onClose} style={{ marginTop: 10 }}>
          {t.close}
        </button>
      </div>
    );
  }

  if (loading) return <div>{t.loading}</div>;
  if (!form) return <div>{t.notFound}</div>;

  return (
    <>
      <form
        className="pei-scope edit-inline-form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        {/* Main */}
        <div className="pei-section">
          <div className="pei-section__title">{t.sectionMain}</div>

          <div className="form-row full-width-row">
            <div className="form-group">
              <label>{t.authors}</label>
              <input
                className="input-text"
                name="authors"
                value={form.authors || ''}
                onChange={onChange}
                placeholder={t.authors}
              />
            </div>
          </div>

          <div className="form-row grid-3-columns">
            <Select name="criteria_form_id" label={t.criteriaForm} source="criteria-forms" />
          </div>
        </div>

        {/* Params */}
        <div className="pei-section">
          <div className="pei-section__title">{t.sectionParams}</div>

          <div className="pei-stats-grid">
            <div className="pei-field-card">
              <label className="pei-field-card__label">{t.sectionCount}</label>
              <input
                className="input-number pei-input"
                type="number"
                name="section_count"
                value={form.section_count || 0}
                readOnly
                onChange={() => {}}
                title={t.sectionCountHint}
              />
              <div className="pei-field-card__hint">{t.sectionCountHint}</div>
            </div>

            <div className="pei-field-card">
              <label className="pei-field-card__label">{t.pageCount}</label>
              <input
                className="input-number pei-input"
                type="number"
                name="page_count"
                value={form.page_count || ''}
                onChange={onChange}
                placeholder={t.pageCount}
              />
            </div>

            <div className="pei-field-card">
              <label className="pei-field-card__label">{t.sheetCount}</label>
              <input
                className="input-number pei-input"
                type="number"
                name="sheet_count"
                value={form.sheet_count || ''}
                onChange={onChange}
                placeholder={t.sheetCount}
              />
            </div>

            <div className="pei-field-card">
              <label className="pei-field-card__label">{t.durationMinutes}</label>
              <input
                className="input-number pei-input"
                type="number"
                name="duration_minutes"
                value={form.duration_minutes || ''}
                onChange={onChange}
                placeholder={t.durationMinutes}
              />
            </div>
          </div>
        </div>

        {/* Archive */}
        <div className="pei-archive">
          <div className="pei-archive__title">{t.sectionArchive}</div>

          <div className="pei-archive__row">
            <input
              className="input-text"
              readOnly
              value={form.archive?.original_name || t.archiveMissing}
            />

            <div className="pei-actions">
              <BusyButton
                busyKey="archive:download"
                className="pei-btn pei-btn--sm pei-btn--primary"
                busyLabel={L.loading}
                onClick={handleDownloadArchive}
                disabled={!form.archive}
              >
                {t.archiveDownload}
              </BusyButton>

              <BusyButton
                busyKey="archive:pick"
                className="pei-btn pei-btn--sm"
                busyLabel={L.loading}
                onClick={async () => {
                  archivePickerRef.current?.click();
                  await wait(180);
                }}
              >
                {form.archive ? t.archiveReplace : t.archiveAdd}
              </BusyButton>

              {pendingArchive && (
                <BusyButton
                  busyKey="archive:clear"
                  className="pei-btn pei-btn--sm pei-btn--danger"
                  busyLabel={L.loading}
                  onClick={clearPendingArchive}
                >
                  {t.archiveClear}
                </BusyButton>
              )}

              <input
                ref={archivePickerRef}
                type="file"
                accept=".zip,application/zip"
                style={{ display: 'none' }}
                onChange={onArchivePicked}
              />
            </div>
          </div>

          {form.archive?.size ? (
            <div className="pei-archive__meta">
              {(form.archive.size / 1024 / 1024).toFixed(2)} MB
              {pendingArchive ? ` • ${t.archiveUnsaved}` : ''}
            </div>
          ) : pendingArchive ? (
            <div className="pei-archive__meta">{t.archiveUnsaved}</div>
          ) : null}
        </div>

        {/* Existing files */}
        <div className="pei-filezone">
          <label className="pei-label-files">{t.filesLoaded}</label>

          <ul className="pei-filelist">
            {existingRows.map((row) => {
              const isDeleted = !!deleteFlags[row.section];

              return (
                <li
                  key={row.id}
                  className="pei-fileitem pei-fileitem--block"
                  style={isDeleted ? { opacity: 0.45 } : undefined}
                >
                  <div className="pei-fileitem__sec">
                    {preferKz ? `${row.section}-бөлім` : `Раздел ${row.section}`}
                    {isDeleted ? (preferKz ? ' • өшіріледі' : ' • будет удалён') : ''}
                  </div>

                  <div className="pei-fileitem__fields">
                    <input
                      className="input-text"
                      value={row.name}
                      disabled={isDeleted}
                      onChange={(e) => setExisting(row.id, { name: e.target.value })}
                      placeholder={t.fileName}
                    />

                    <input
                      className="input-text"
                      value={row.link_url || ''}
                      disabled={isDeleted}
                      onChange={(e) => {
                        const value = e.target.value;

                        // если вводят ссылку в existing — убираем выбранный replace file
                        setReplaceBySection((prev) => {
                          const copy = { ...prev };
                          delete copy[row.section];
                          return copy;
                        });

                        setExisting(row.id, {
                          link_url: value,
                          is_link: !!value.trim(),
                          type: value.trim() ? 'link' : 'upload',
                        });

                        setDeleteFlag(row.section, false);
                      }}
                      placeholder={t.fileLink}
                    />
                  </div>

                  {(row.link_url || row.is_link) && !isDeleted && (
                    <div className="pei-actions" style={{ marginTop: 4 }}>
                      <BusyButton
                        busyKey={`archive:exist:${row.section}`}
                        className="pei-btn pei-btn--sm"
                        busyLabel={L.loading}
                        onClick={async () => {
                          existingArchivePickers.current[row.section]?.click();
                          await wait(180);
                        }}
                      >
                        {existingArchives[row.section]
                          ? existingArchives[row.section].name
                          : row.section_archive_name
                            ? row.section_archive_name
                            : (preferKz ? 'Секция архиві (ZIP)' : 'Архив секции (ZIP)')}
                      </BusyButton>
                      <input
                        ref={(el) => (existingArchivePickers.current[row.section] = el)}
                        type="file"
                        accept=".zip,.rar,.7z"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          e.target.value = '';
                          if (!f) return;
                          setExistingArchives((prev) => ({ ...prev, [row.section]: f }));
                        }}
                      />
                    </div>
                  )}

                  <div className="pei-actions">
                    {!row.link_url && !row.is_link && (
                      <>
                        <BusyButton
                          busyKey={`pick:exist:${row.section}`}
                          className="pei-btn pei-btn--sm"
                          busyLabel={L.loading}
                          disabled={isDeleted}
                          onClick={async () => {
                            replacePickers.current[row.section]?.click();
                            await wait(180);
                          }}
                        >
                          {t.filePick}
                        </BusyButton>

                        <input
                          ref={(el) => (replacePickers.current[row.section] = el)}
                          type="file"
                          style={{ display: 'none' }}
                          accept=".pdf,.doc,.docx"
                          onChange={(e) => onReplacePicked(row.section, e)}
                        />

                        <span className="pei-hint">{replaceBySection[row.section]?.name || ''}</span>
                      </>
                    )}


                    <span className="pei-hint">{replaceBySection[row.section]?.name || ''}</span>

                    <BusyButton
                      busyKey={`dl:${row.id}`}
                      className="pei-btn pei-btn--sm pei-btn--primary"
                      busyLabel={L.loading}
                      onClick={() =>
                        handleDownloadFile({
                          id: row.id,
                          link_url: row.link_url,
                          name: row.name,
                          url: row.url,
                        })
                      }
                    >
                      {t.fileDownload}
                    </BusyButton>

                    <BusyButton
                      busyKey={`del:${row.section}`}
                      className="pei-btn pei-btn--sm pei-btn--danger"
                      busyLabel={L.deleting}
                      onClick={() => deleteSectionNow(row.section)}
                    >
                      {isDeleted ? t.fileUndoDelete : t.fileDelete}
                    </BusyButton>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* New files */}
        <div className="pei-section">
          <div className="pei-section__title">{t.sectionNewFiles}</div>

          <div className="pei-newfiles-actions" style={{ marginTop: 4 }}>
            <BusyButton
              busyKey="row:add"
              className="pei-btn pei-btn--sm pei-btn--primary"
              busyLabel={L.adding}
              onClick={async () => {
                addNewRow();
                await wait(220);
              }}
            >
              + {t.addFile}
            </BusyButton>
          </div>

          {newRows.length > 0 && (
            <div className="pei-newrows" style={{ marginTop: 10 }}>
              <ul className="pei-newrows__list">
                {newRows.map((r, idx) => (
                  <li key={r.uid} className="pei-newrows__item">
                    <div className="pei-newrows__head">{idx + 1}</div>

                    <div className="pei-newrows__fields">
                      <input
                        className="input-text"
                        value={r.name}
                        onChange={(e) => setNewRow(r.uid, { name: e.target.value })}
                        placeholder={t.fileOrLinkName}
                      />

                      <div className="pei-newrows__pick">
                        <BusyButton
                          busyKey={`pick:new:${r.uid}`}
                          className="pei-btn pei-btn--sm"
                          busyLabel={L.loading}
                          onClick={async () => {
                            newPickers.current[r.uid]?.click();
                            await wait(180);
                          }}
                        >
                          {t.filePick}
                        </BusyButton>

                        <input
                          ref={(el) => (newPickers.current[r.uid] = el)}
                          type="file"
                          accept=".pdf,.doc,.docx"
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            e.target.value = '';
                            if (!f) return;
                            setNewRow(r.uid, { file: f, name: r.name || f.name, link_url: '' });
                          }}
                        />

                        <span className="pei-newrows__filename">
                          {r.file ? r.file.name : t.noFileChosen}
                        </span>
                      </div>

                      <input
                        className="input-text"
                        value={r.link_url}
                        onChange={(e) =>
                          setNewRow(r.uid, {
                            link_url: e.target.value,
                            file: e.target.value ? null : r.file,
                          })
                        }
                        placeholder={t.fileLinkForElectronic}
                      />
                      {r.link_url && (
  <div className="pei-newrows__pick">
    <BusyButton
      busyKey={`archive:new:${r.uid}`}
      className="pei-btn pei-btn--sm"
      busyLabel={L.loading}
      onClick={async () => {
        newPickers.current[`archive_${r.uid}`]?.click();
        await wait(180);
      }}
    >
      {r.section_archive
        ? r.section_archive.name
        : (preferKz ? 'Секция архиві (ZIP)' : 'Архив секции (ZIP)')}
    </BusyButton>
    <input
      ref={(el) => (newPickers.current[`archive_${r.uid}`] = el)}
      type="file"
      accept=".zip,.rar,.7z"
      style={{ display: 'none' }}
      onChange={(e) => {
        const f = e.target.files?.[0];
        e.target.value = '';
        if (!f) return;
        setNewRow(r.uid, { section_archive: f });
      }}
    />
  </div>
)}
                    </div>

                    <div className="pei-newrows__aside">
                      <label className="pei-newrows__label">{t.fileSection}</label>
                      <input
                        type="number"
                        min={1}
                        className="input-number"
                        value={r.section}
                        onChange={(e) =>
                          setNewRow(r.uid, { section: e.target.value ? Number(e.target.value) : '' })
                        }
                        placeholder="№"
                        style={{ width: 90 }}
                      />

                      <BusyButton
                        busyKey={`new:remove:${r.uid}`}
                        className="pei-btn pei-btn--sm pei-btn--danger"
                        busyLabel={L.deleting}
                        onClick={async () => {
                          removeNewRow(r.uid);
                          await wait(120);
                        }}
                        style={{ marginTop: 6 }}
                      >
                        ×
                      </BusyButton>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Message */}
        <div className="pei-section">
          <div className="pei-section__title">{t.sectionMessage}</div>
          <textarea
            className="textarea-message"
            name="message"
            value={form.message || ''}
            onChange={onChange}
            placeholder={t.messagePlaceholder}
          />
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className={`btn-save-Publisher ${saving ? 'is-loading' : ''}`}
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="btn-spinner" /> {L.saving}
              </>
            ) : (
              t.save
            )}
          </button>

          <button type="button" className="btn-close-Publisher" onClick={onClose}>
            {t.close}
          </button>
        </div>
      </form>
    

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </>
  );
}